import React, { useState } from 'react';
import { Lock, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import axios from 'axios';
import { setAuthToken, setUser } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PortalLogin = ({ subtitle, icon: Icon, allowedRoles, onSuccess, testIdPrefix }) => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/dispatch/login`, formData);
      const { access_token, user } = response.data;

      if (access_token && allowedRoles.includes(user.role)) {
        setAuthToken(access_token);
        setUser(user);
        onSuccess(user);
      } else {
        setError(`This account does not have access to the ${subtitle}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8" data-testid={`${testIdPrefix}-login-card`}>
        <div className="text-center mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-600 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Icon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Sabey Logistics</h1>
          <p className="text-slate-600" data-testid={`${testIdPrefix}-login-subtitle`}>{subtitle}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm" data-testid={`${testIdPrefix}-login-error`}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Username</label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Enter your username"
                className="pl-10 border-slate-300"
                data-testid={`${testIdPrefix}-login-username`}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter your password"
                className="pl-10 border-slate-300"
                data-testid={`${testIdPrefix}-login-password`}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-slate-800 to-slate-600 hover:from-slate-700 hover:to-slate-500 text-white py-3 text-lg"
            data-testid={`${testIdPrefix}-login-submit`}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default PortalLogin;
